import { QueryHookOptions, QueryResult } from '@apollo/client'
import keyBy from 'lodash/keyBy'
import { useMemo } from 'react'
import { z } from 'zod'

import { isExistent } from '@aletheia/common/utils/guards'

export function safeParseFloat(num: string | number): number
export function safeParseFloat(num: null): undefined
export function safeParseFloat(
  num?: string | number | null | undefined,
): number | undefined
export function safeParseFloat(
  num?: string | number | null | undefined,
): number | undefined {
  if (!num) return
  if (typeof num === 'number') return num
  if (typeof num === 'string') return parseFloat(num)
}

/**
 * Wraps a GraphQL hook with a new hook that runs a reducer function. The
 * wrapped function now returns a tuple of [reducer(QueryResult), QueryResult]
 *
 * This is mostly useful to add sugar over the top of useQuery-derived
 * functions, which return their results as a deeply-nested object, e.g.
 *
 * `typeof result = data?: { Organizations?: {
 *     edges?: { node: Organization }[] } }`
 *
 * The reducer function can be used to return a simplified data structure that's
 * easier to use in components:
 *
 * `typeof reducer(result) = Organization[]`
 *
 * This is particularly powerful when combined with `flattenGQLArrayResult` in
 * the reducer.
 *
 * @example
 *   export const useGetOrganizationsQueryWithReducer = makeQueryWithReducer(
 *     useGetOrganizationsQuery,
 *     (result) => flattenGQLArrayResult(result.data?.Organizations),
 *   )
 *
 * @param useQueryHook A query function based on Apollo's useQuery
 *     function. Typically this will be a `useSomething` function from the
 *     auto-generated GraphQL file
 * @param reducer A function that receives the results of useQueryHook and
 *     applies a transformation to them
 */
type BaseOptions<OptionData, OptionVariables> =
  | QueryHookOptions<OptionData, OptionVariables>
  | (() => QueryHookOptions<OptionData, OptionVariables>)

export function makeQueryWithReducer<
  OptionData,
  OptionVariables,
  Result extends QueryResult,
  ReducedResult,
>(
  useQueryHook: (
    options: QueryHookOptions<OptionData, OptionVariables>,
  ) => Result,
  reducer: (result: Result) => ReducedResult,
  baseOptions?: BaseOptions<OptionData, OptionVariables>,
): (
  options?: QueryHookOptions<OptionData, OptionVariables>,
) => [ReducedResult | undefined, Result] {
  return function useReducedQuery(
    options?: QueryHookOptions<OptionData, OptionVariables>,
  ): [ReducedResult | undefined, Result] {
    let _baseOptions: QueryHookOptions<OptionData, OptionVariables> | undefined
    // base options arg can be a function
    if (typeof baseOptions === 'function') _baseOptions = baseOptions()
    else _baseOptions = baseOptions
    // build args
    let _options: QueryHookOptions<OptionData, OptionVariables> | undefined
    if (_baseOptions) _options = { ..._baseOptions }
    if (options) _options = { ..._options, ...options }
    const result = useQueryHook(_options!)
    // preserve referential equality in the reducer result
    // if we don't memoize this, then the value is recomputed on
    // every render, and we lose our nice referential equality
    // that's otherwise provided by the Apollo cache
    const reducedResult = useMemo(() => reducer(result), [result])
    return [reducedResult, result]
  }
}

type TGQLNodeResult<N> = {
  edges?:
    | {
        node?: N | null | undefined
      }[]
    | null
}

/**
 * Function for flattening an array-returning GraphQL query. Takes the query
 * node that contains the results (typically located at
 * `result.data.MyObjectName`). E.g. from:
 *
 * `MyObjectName.edges[].node`
 *
 * ... to:
 *
 * `node[]`
 *
 * @param resultNodeRoot The property of the query result object that contains
 *     the data. Should have an `edges` property, which is an array of `{node}`
 */
export function flattenGQLArrayResult<N>(
  resultNodeRoot: TGQLNodeResult<N> | null | undefined,
): N[] | undefined {
  return resultNodeRoot?.edges?.map((edge) => edge.node).filter(isExistent)
}

/**
 * Function to pass the result of a reducer through a Zod parser for additional
 * type-checking and parsing
 *
 * @param reducer Reducer function to extract final data from query
 * @param parser Zod parser that parses result of reducer function into the
 * final return shape
 * @returns
 */
export function parseReducerResult<R, X, P>(
  reducer: (result: R) => X | undefined,
  parser: z.ZodType<P, z.ZodTypeDef, unknown>,
): (result: R) => P | undefined {
  return (result: R) => {
    const reducedResult = reducer(result)
    return reducedResult && parser.parse(reducedResult)
  }
}

/**
 * Similar to `flattenGQLArrayResult()`, but returns an object, keyed by a given
 * string. This is useful for returning hashmaps of a query result for faster
 * lookups.
 *
 * @param resultNodeRoot The property of the query result object that contains
 *     the data. Should have an `edges` property, which is an array of `{node}`
 * @param key A string key of the base object type returned by the query
 * @see {flattenGQLArrayResult}
 */
export function flattenGQLArrayResultKeyed<N>(
  resultNodeRoot: TGQLNodeResult<N> | null | undefined,
  key: keyof N,
): { [x: string]: N } | undefined {
  return keyBy(flattenGQLArrayResult(resultNodeRoot), key)
}

/** Guard to test if an object has a non-null ID */
export type PossiblyNullID = {
  id?: string | null | undefined
  [x: string]: unknown
}
export type NonNullID<T extends PossiblyNullID> = T & { id: string }
export function isObjectWithId<T extends PossiblyNullID>(
  obj: T,
): obj is NonNullID<T> {
  return typeof obj.id === 'string'
}
